import React, { useEffect, useState, useCallback } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Loader2, Search, Wallet, ArrowUpRight, CheckCircle, X
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `NGN ${Number(n || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;

export default function WalletCredit() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState('');
  const [narration, setNarration] = useState('');
  const [crediting, setCrediting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const data = await superAdminAPI.getEmployeesWallets(q);
      setEmployees(data);
    } catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const handleCredit = async () => {
    if (!selected || !amount || parseFloat(amount) <= 0) {
      toast.error('Select an employee and enter a valid amount');
      return;
    }
    setCrediting(true);
    try {
      const res = await superAdminAPI.creditWallet({
        employee_id: selected.id,
        amount: parseFloat(amount),
        narration: narration || `Admin credit for ${selected.name}`,
      });
      setLastResult(res);
      toast.success(`Credited ${fmt(res.amount_credited)} to ${res.employee}`);
      setAmount('');
      setNarration('');
      setSelected(null);
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Credit failed');
    } finally { setCrediting(false); }
  };

  return (
    <div className="space-y-6" data-testid="wallet-credit-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Credit Wallet</h1>
        <p className="text-sm text-gray-500">Fund any employee wallet instantly</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Employee List */}
        <div className="lg:col-span-3 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search by name or email..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-9 pl-9 border-gray-200" data-testid="wallet-search" />
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-[11px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-500">Employee</th>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-500 hidden sm:table-cell">Company</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500">Balance</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 w-20">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {employees.map(emp => (
                    <tr key={emp.id} className={`hover:bg-gray-50/50 transition-colors ${selected?.id === emp.id ? 'bg-pink-50' : ''}`}>
                      <td className="py-2.5 px-3">
                        <p className="font-medium text-gray-900">{emp.name}</p>
                        <p className="text-[10px] text-gray-400">{emp.email}</p>
                      </td>
                      <td className="py-2.5 px-3 text-gray-500 hidden sm:table-cell">{emp.company}</td>
                      <td className="py-2.5 px-3 text-right font-semibold text-gray-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {fmt(emp.balance)}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <Button size="sm" variant={selected?.id === emp.id ? 'default' : 'outline'}
                          className={`h-7 text-[10px] px-2 ${selected?.id === emp.id ? 'bg-pink-500 hover:bg-pink-600 text-white' : ''}`}
                          onClick={() => setSelected(selected?.id === emp.id ? null : emp)}
                          data-testid={`select-${emp.id}`}>
                          {selected?.id === emp.id ? 'Selected' : 'Credit'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!employees.length && (
                    <tr><td colSpan={4} className="text-center py-8 text-gray-400 text-xs">No employees found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Credit Panel */}
        <div className="lg:col-span-2">
          <Card className={`border-2 transition-colors ${selected ? 'border-pink-300 bg-pink-50/20' : 'border-gray-200'}`}>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-pink-500" />
                <h2 className="text-sm font-bold text-gray-900">Credit Wallet</h2>
              </div>

              {selected ? (
                <>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-gray-900">{selected.name}</p>
                        <p className="text-[10px] text-gray-400">{selected.email} | {selected.company}</p>
                      </div>
                      <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Current balance: <span className="font-bold text-gray-800" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmt(selected.balance)}</span>
                    </p>
                  </div>

                  <div>
                    <Label className="text-xs font-medium text-gray-600">Amount (NGN)</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">N</span>
                      <Input type="text" inputMode="numeric" value={amount}
                        onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                        placeholder="0.00" className="h-10 pl-7 text-lg font-bold"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                        data-testid="credit-amount" />
                    </div>
                    {/* Quick amount buttons */}
                    <div className="flex gap-1.5 mt-2">
                      {[5000, 10000, 50000, 100000, 500000].map(a => (
                        <button key={a} onClick={() => setAmount(String(a))}
                          className="flex-1 text-[9px] py-1 rounded bg-gray-100 hover:bg-pink-100 text-gray-600 hover:text-pink-700 font-medium transition-colors">
                          {a >= 1000 ? `${a/1000}K` : a}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-medium text-gray-600">Narration</Label>
                    <Input value={narration} onChange={e => setNarration(e.target.value)}
                      placeholder="e.g. Salary advance, bonus" className="h-9 mt-1 text-xs"
                      data-testid="credit-narration" />
                  </div>

                  <Button className="w-full bg-pink-500 hover:bg-pink-600 text-white h-10"
                    disabled={!amount || parseFloat(amount) <= 0 || crediting}
                    onClick={handleCredit} data-testid="credit-submit">
                    {crediting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowUpRight className="w-4 h-4 mr-2" />}
                    Credit {amount ? fmt(parseFloat(amount)) : 'Wallet'}
                  </Button>
                </>
              ) : (
                <div className="text-center py-8">
                  <Wallet className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-400">Select an employee to credit their wallet</p>
                </div>
              )}

              {/* Last credit result */}
              {lastResult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-3" data-testid="credit-result">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <p className="text-xs font-semibold text-emerald-800">Credit Successful</p>
                  </div>
                  <div className="space-y-1 text-[10px] text-emerald-700">
                    <p>Employee: <span className="font-semibold">{lastResult.employee}</span></p>
                    <p>Amount: <span className="font-semibold">{fmt(lastResult.amount_credited)}</span></p>
                    <p>New Balance: <span className="font-semibold">{fmt(lastResult.new_balance)}</span></p>
                    <p>Reference: <span className="font-mono">{lastResult.reference}</span></p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
